import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, ChevronDown, X, ImageOff, Loader2 } from "lucide-react";
import { ActressCard } from "@/components/ActressCard.tsx";
import { FilterPanel } from "@/components/FilterPanel.tsx";
import { PaginationNav } from "@/components/PaginationNav.tsx";
import { fetchActresses, fetchFeaturedActresses, fetchTags } from "@/lib/api.ts";
import { DEFAULT_FILTER, SORT_OPTIONS, AGE_GROUPS, type FilterState } from "@/lib/types.ts";
import type { Actress } from "@/lib/types.ts";

export function TopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<FilterState>(() => {
    const sortParam = searchParams.get("sort");
    return {
      ...DEFAULT_FILTER,
      sortBy: (sortParam as FilterState["sortBy"]) || DEFAULT_FILTER.sortBy,
    };
  });
  const [actresses, setActresses] = useState<Actress[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [sortOpen, setSortOpen] = useState(false);
  const [tagNames, setTagNames] = useState<string[]>([]);
  const [showNoImage, setShowNoImage] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTags().then((tags) => setTagNames(tags.map((t) => t.name)));
  }, []);

  const isDefaultView =
    !filter.search &&
    !selectedTag &&
    !showNoImage &&
    filter.tags.length === 0 &&
    filter.sortBy === DEFAULT_FILTER.sortBy &&
    filter.ageType === DEFAULT_FILTER.ageType &&
    filter.cupSizes.length === DEFAULT_FILTER.cupSizes.length &&
    filter.cupSizes.every((c) => DEFAULT_FILTER.cupSizes.includes(c)) &&
    filter.ageGroups.length === DEFAULT_FILTER.ageGroups.length &&
    filter.ageGroups.every((a) => DEFAULT_FILTER.ageGroups.includes(a)) &&
    filter.bustRange[0] === DEFAULT_FILTER.bustRange[0] &&
    filter.bustRange[1] === DEFAULT_FILTER.bustRange[1] &&
    filter.waistRange[0] === DEFAULT_FILTER.waistRange[0] &&
    filter.waistRange[1] === DEFAULT_FILTER.waistRange[1] &&
    filter.hipRange[0] === DEFAULT_FILTER.hipRange[0] &&
    filter.hipRange[1] === DEFAULT_FILTER.hipRange[1] &&
    filter.heightRange[0] === DEFAULT_FILTER.heightRange[0] &&
    filter.heightRange[1] === DEFAULT_FILTER.heightRange[1];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (isDefaultView) {
        const result = await fetchFeaturedActresses(page);
        setActresses(result.data);
        setTotal(result.total);
        setTotalPages(result.totalPages);
      } else {
        const activeFilter = selectedTag
          ? { ...filter, tags: [selectedTag] }
          : filter;
        const result = await fetchActresses(activeFilter, page, 12, !showNoImage);
        setActresses(result.data);
        setTotal(result.total);
        setTotalPages(result.totalPages);
      }
    } finally {
      setLoading(false);
    }
  }, [filter, page, selectedTag, showNoImage, isDefaultView]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFilterChange = (newFilter: FilterState) => {
    setFilter(newFilter);
    setPage(1);
  };

  const handleSortChange = (sortBy: FilterState["sortBy"]) => {
    setFilter((prev) => ({ ...prev, sortBy }));
    setSortOpen(false);
    setPage(1);
    if (sortBy === "newest") {
      setSearchParams({ sort: "newest" });
    } else {
      setSearchParams({});
    }
  };

  const handleSearch = (value: string) => {
    setFilter((prev) => ({ ...prev, search: value }));
    setPage(1);
  };

  const handleTagClick = (tag: string | null) => {
    setSelectedTag(tag);
    setPage(1);
  };

  const handleAgeGroupClick = (value: string | null) => {
    setFilter((prev) => ({
      ...prev,
      ageGroups: value ? [value] : [],
    }));
    setPage(1);
  };

  const selectedAgeGroup = filter.ageGroups.length === 1 ? filter.ageGroups[0] : null;
  const displayTags = ["すべて", ...tagNames.slice(0, 6)];

  return (
    <div className="flex flex-col flex-1">
      {/* Filter Panel - Overlay */}
      {filterOpen && (
        <div className="fixed inset-0 z-50 flex">
          <FilterPanel
            filter={filter}
            onFilterChange={handleFilterChange}
            onClose={() => setFilterOpen(false)}
          />
          <div
            className="flex-1 bg-black/30"
            onClick={() => setFilterOpen(false)}
          />
        </div>
      )}

      {/* Search & Filter Bar */}
      <div className="w-full bg-background px-8 lg:px-16 py-8 flex flex-col gap-5">
        <div className="flex gap-4 items-center">
          <div className="flex-1 flex items-center gap-3 h-12 px-5 bg-card border border-border rounded-full">
            <Search size={20} className="text-muted-foreground shrink-0" />
            <input
              type="text"
              placeholder="名前・タグで検索..."
              value={filter.search}
              onChange={(e) => handleSearch(e.target.value)}
              className="flex-1 bg-transparent border-0 outline-none font-[var(--font-secondary)] text-[15px] text-foreground placeholder:text-muted-foreground"
            />
            {filter.search && (
              <button
                onClick={() => handleSearch("")}
                className="text-muted-foreground hover:text-foreground bg-transparent border-0 cursor-pointer"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <button
            onClick={() => setFilterOpen(true)}
            className="flex items-center gap-2 h-12 px-5 bg-secondary rounded-full border-0 cursor-pointer hover:opacity-80 transition-opacity"
          >
            <SlidersHorizontal size={18} className="text-secondary-foreground" />
            <span className="font-[var(--font-primary)] text-sm font-medium text-secondary-foreground">
              フィルター
            </span>
          </button>
        </div>

        <div className="flex gap-2 flex-wrap">
          {displayTags.map((tag, i) => (
            <button
              key={tag}
              onClick={() => handleTagClick(i === 0 ? null : tag)}
              className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium border-0 cursor-pointer transition-colors ${
                (i === 0 && selectedTag === null) || tag === selectedTag
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground hover:bg-muted"
              }`}
            >
              {tag}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-2 flex-wrap items-center">
            <div className="flex bg-secondary rounded-full overflow-hidden mr-1">
              <button
                onClick={() => setFilter((prev) => ({ ...prev, ageType: "real" }))}
                className={`px-3 py-1.5 text-[12px] font-medium border-0 cursor-pointer transition-colors ${
                  filter.ageType === "real"
                    ? "bg-foreground text-background"
                    : "bg-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                実年齢
              </button>
              <button
                onClick={() => setFilter((prev) => ({ ...prev, ageType: "active" }))}
                className={`px-3 py-1.5 text-[12px] font-medium border-0 cursor-pointer transition-colors ${
                  filter.ageType === "active"
                    ? "bg-foreground text-background"
                    : "bg-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                出演年齢
              </button>
            </div>
            <button
              onClick={() => handleAgeGroupClick(null)}
              className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium border-0 cursor-pointer transition-colors ${
                !selectedAgeGroup
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground hover:bg-muted"
              }`}
            >
              全年代
            </button>
            {AGE_GROUPS.map((ag) => (
              <button
                key={ag.value}
                onClick={() => handleAgeGroupClick(ag.value)}
                className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium border-0 cursor-pointer transition-colors ${
                  selectedAgeGroup === ag.value
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-secondary-foreground hover:bg-muted"
                }`}
              >
                {ag.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <button
              onClick={() => setSortOpen(!sortOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-full cursor-pointer"
            >
              <span className="font-[var(--font-secondary)] text-[13px] text-foreground">
                {SORT_OPTIONS.find((o) => o.value === filter.sortBy)?.label}
              </span>
              <ChevronDown size={16} className="text-muted-foreground" />
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-2 bg-card border border-border rounded-xl shadow-lg z-10 overflow-hidden min-w-[160px]">
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleSortChange(option.value)}
                    className={`w-full text-left px-4 py-2.5 border-0 cursor-pointer font-[var(--font-secondary)] text-sm transition-colors ${
                      filter.sortBy === option.value
                        ? "bg-primary/10 text-primary"
                        : "bg-transparent text-foreground hover:bg-secondary"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results Count + No-Image Toggle */}
      <div className="px-8 lg:px-16 pb-2 flex items-center justify-between">
        <span className="font-[var(--font-secondary)] text-sm text-muted-foreground">
          {total.toLocaleString()} 件の結果
        </span>
        <button
          onClick={() => { setShowNoImage(!showNoImage); setPage(1); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium border cursor-pointer transition-colors ${
            showNoImage
              ? "bg-primary/10 text-primary border-primary/30"
              : "bg-transparent text-muted-foreground border-border hover:bg-secondary"
          }`}
        >
          <ImageOff size={14} />
          サムネなしも表示
        </button>
      </div>

      {/* Grid */}
      <div className="px-8 lg:px-16 pb-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {actresses.map((actress) => (
                <ActressCard key={actress.id} actress={actress} />
              ))}
            </div>
            {actresses.length === 0 && (
              <div className="text-center py-20 text-muted-foreground font-[var(--font-secondary)]">
                該当する女優が見つかりませんでした
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination */}
      <PaginationNav currentPage={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
}
